import { Loader2 } from 'lucide-react'

import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type { ComponentProps, ReactNode } from 'react'

const loadingStateSizes = {
  compact: 'min-h-24 gap-2 px-4 py-5',
  default: 'min-h-32 gap-3 px-4 py-8',
  spacious: 'min-h-56 gap-3 px-6 py-10',
}

const loadingStateSurfaces = {
  muted: 'border-border/50 bg-muted/25',
  card: 'border-border/50 bg-card',
  transparent: 'border-border/40 bg-transparent',
}

export type LoadingStateSize = keyof typeof loadingStateSizes

export type LoadingStateSurface = keyof typeof loadingStateSurfaces

export type LoadingStateProps = ComponentProps<'div'> & {
  label?: ReactNode
  size?: LoadingStateSize
  surface?: LoadingStateSurface
}

function LoadingState({
  label = 'Loading…',
  size = 'default',
  surface = 'muted',
  className,
  ...props
}: LoadingStateProps) {
  return (
    <div
      data-slot="loading-state"
      data-size={size}
      data-surface={surface}
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border text-center',
        loadingStateSizes[size] ?? loadingStateSizes.default,
        loadingStateSurfaces[surface] ?? loadingStateSurfaces.muted,
        className
      )}
      {...props}
    >
      <Loader2 className="size-5 animate-spin text-interaction" aria-hidden="true" />
      <Typography variant="label" tone="muted">
        {label}
      </Typography>
    </div>
  )
}

export { LoadingState }
